<template>
  <div class="top-stats-panel kop-panel">
    <div class="top-stats-heading">
      <span class="eyebrow">Weekly Statistics</span>
      <strong>This Week</strong>
      <span class="data-freshness" :title="dataFreshnessTitle">
        <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status"></span>
        <template v-else>{{ statusLabel }}</template>
      </span>
    </div>

    <div v-if="phase === 'idle'" class="top-stats-note">
      <span>Weekly statistics are built from the match history loaded on the</span>
      <router-link class="note-link" to="/">Home page</router-link>
      <span>.</span>
    </div>

    <div v-else-if="phase === 'error'" class="top-stats-note">
      <span class="note-error">{{ errorLabel }}</span>
      <router-link class="note-link" to="/">Retry from Home</router-link>
    </div>

    <template v-else>
      <div v-if="coverageNote" class="top-stats-note">
        <span>{{ coverageNote }}</span>
      </div>

      <div class="top-stats-row">
        <div v-for="stat in statsCards" :key="stat.label" class="stat-card" :title="stat.title || null">
          <span class="stat-label">{{ stat.label }}</span>
          <strong class="stat-value">{{ stat.value }}</strong>
        </div>
      </div>

      <div class="mvp-strip">
        <span class="mvp-label">MVP</span>
        <strong v-if="summary.mvp" class="mvp-value">
          <PlayerName :player-id="summary.mvp.playerId" :fallback-name="summary.mvp.name" :link="false" />
          <span>&nbsp;({{ summary.mvp.winrate }}% WR, {{ summary.mvp.games }}G)</span>
        </strong>
        <strong v-else class="mvp-value">{{ rankedLoaded ? 'None' : '—' }}</strong>
      </div>
    </template>
  </div>
</template>

<script>
import { markRaw } from 'vue'
import MatchesCache from '@/service/matchescache.js'
import PlayerName from '@/components/player_name.vue'
import { MINIMUM_VERSION } from '@/constants/Version'
import { computeWeeklySummary, emptyWeeklySummary, getNextWeekStart } from '@/service/weeklystats.js'
import { startTimer } from '@/service/performance.js'

// Timers cannot exceed a signed 32-bit millisecond count; a week fits, but a
// clock that is badly off must not produce an immediate or negative delay.
const MAX_TIMEOUT = 2147483647

const describeError = (error) => {
  if (!error) return ''
  if (typeof error === 'string') return error
  return error.message || 'request failed'
}

export default {
  name: 'KopTopStatsBar',
  components: {
    PlayerName,
  },
  data() {
    return {
      // Plain summary published once per recompute; never the match arrays.
      summary: markRaw(emptyWeeklySummary()),
      // Bumped on a timer so the relative "x ago" label keeps up without a
      // reload. This only re-renders the label; it never rescans matches.
      now: Date.now(),
    }
  },

  computed: {
    ranked() {
      return MatchesCache.state.ranked
    },
    normal() {
      return MatchesCache.state.normal
    },
    rankedLoaded() {
      return Boolean(this.ranked.loaded)
    },
    normalLoaded() {
      return Boolean(this.normal.loaded)
    },
    anyLoaded() {
      return this.rankedLoaded || this.normalLoaded
    },
    isLoading() {
      return Boolean(this.ranked.loading || this.normal.loading)
    },

    // The bar never requests data. It reports what the cache currently holds:
    // idle (nothing requested yet), error (nothing loaded and a request
    // failed), or a summary that may cover one or both modes.
    phase() {
      if (this.anyLoaded) return 'ready'
      if (this.isLoading) return 'loading'
      if (this.ranked.error || this.normal.error) return 'error'
      return 'idle'
    },

    errorLabel() {
      const failed = []
      if (this.ranked.error) failed.push(`ranked (${describeError(this.ranked.error)})`)
      if (this.normal.error) failed.push(`normal (${describeError(this.normal.error)})`)
      return `Could not load ${failed.join(' and ')} match history.`
    },

    // Shown when the summary is built from a single mode.
    coverageNote() {
      if (this.rankedLoaded && this.normalLoaded) return ''

      const missing = this.rankedLoaded ? this.normal : this.ranked
      const name = this.rankedLoaded ? 'Normal' : 'Ranked'
      const consequence = this.rankedLoaded
        ? 'totals cover ranked games only.'
        : 'hero, kill and MVP stats need ranked games.'

      if (missing.loading) return `${name} matches still loading; ${consequence}`
      if (missing.error) return `${name} matches failed to load (${describeError(missing.error)}); ${consequence}`
      return `${name} matches not loaded; ${consequence}`
    },

    // How current the data on screen is. Taken from the newest match in the
    // payload rather than from when it was downloaded, so a stale cache - at
    // the edge or in the browser - shows up honestly as an older timestamp.
    dataFreshness() {
      const latest = MatchesCache.latestMatchAt.value
      if (!latest) return ''

      const seconds = Math.max(0, Math.floor((this.now - latest.getTime()) / 1000))
      if (seconds < 120) return 'Latest match just now'

      const minutes = Math.floor(seconds / 60)
      if (minutes < 60) return `Latest match ${minutes}m ago`

      const hours = Math.floor(minutes / 60)
      if (hours < 24) return `Latest match ${hours}h ago`

      const days = Math.floor(hours / 24)
      return `Latest match ${days}d ago`
    },

    statusLabel() {
      if (this.phase === 'idle') return 'Not loaded'
      if (this.phase === 'error') return 'Unavailable'
      return this.dataFreshness
    },

    dataFreshnessTitle() {
      const latest = MatchesCache.latestMatchAt.value
      if (latest) return `Most recent match in this data: ${latest.toLocaleString()}`
      if (this.phase === 'loading') return 'Loading match data'
      if (this.phase === 'error') return 'Match data could not be loaded'
      return 'Match data has not been requested on this page'
    },

    statsCards() {
      const s = this.summary
      const partialTotal = this.rankedLoaded !== this.normalLoaded
      return [
        {
          label: partialTotal ? 'Games (partial)' : 'Total Games',
          value: this.anyLoaded ? s.totalGames : '—',
          title: partialTotal ? this.coverageNote : '',
        },
        { label: 'Ranked Games', value: this.rankedLoaded ? s.rankedGames : '—' },
        { label: 'Most Played', value: this.rankedLoaded ? s.mostPlayedHero || 'None' : '—' },
        { label: 'Most Banned', value: this.rankedLoaded ? s.mostBannedHero || 'None' : '—' },
        { label: 'Kills', value: this.rankedLoaded ? s.kills : '—' },
      ]
    },
  },

  created() {
    this.freshnessTimer = null
    this.weekTimer = null
    this.unmounted = false
  },

  mounted() {
    MatchesCache.onChange(this.handleCacheUpdate)
    this.recompute()
    this.freshnessTimer = setInterval(() => {
      this.now = Date.now()
    }, 60000)
    this.scheduleWeekRollover()
  },

  beforeUnmount() {
    this.unmounted = true
    MatchesCache.offChange(this.handleCacheUpdate)
    clearInterval(this.freshnessTimer)
    clearTimeout(this.weekTimer)
  },

  methods: {
    // Recompute only when a mode is published or the local week rolls over.
    recompute() {
      if (this.unmounted) return
      const now = new Date()
      const ranked = this.rankedLoaded ? MatchesCache.getMatches(MINIMUM_VERSION, true, false) : []
      const normal = this.normalLoaded ? MatchesCache.getMatches(MINIMUM_VERSION, false, false) : []
      const stop = startTimer('topbar:weeklySummary')
      const summary = computeWeeklySummary({ ranked, normal, now })
      stop({ scanned: ranked.length + normal.length, weekGames: summary.totalGames })
      this.summary = markRaw(summary)
    },

    handleCacheUpdate() {
      this.recompute()
    },

    // The weekly figures can only change on their own at local Monday
    // midnight, so that is the single moment a timer needs to fire.
    scheduleWeekRollover() {
      clearTimeout(this.weekTimer)
      const delay = getNextWeekStart(new Date()).getTime() - Date.now()
      this.weekTimer = setTimeout(() => {
        this.recompute()
        this.scheduleWeekRollover()
      }, Math.min(Math.max(delay, 1000), MAX_TIMEOUT))
    },
  },
}
</script>

<style scoped>
.data-freshness {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  color: #9fb4ba;
  font-size: 0.78rem;
  white-space: nowrap;
}

.data-freshness .spinner-border-sm {
  width: 0.8rem;
  height: 0.8rem;
  border-width: 0.13em;
}

.top-stats-panel {
  position: relative;
  display: grid;
  gap: 8px;
  margin-bottom: 0.8rem;
  padding: 9px 10px;
  overflow: hidden;
}

.top-stats-panel::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    90deg,
    rgba(255, 191, 102, 0.08),
    transparent 28%,
    transparent 72%,
    rgba(98, 214, 144, 0.08)
  );
}

.top-stats-heading,
.top-stats-row,
.top-stats-note,
.mvp-strip {
  position: relative;
}

.top-stats-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  padding: 0 4px;
}

.top-stats-note {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 4px;
  min-height: 30px;
  padding: 4px 10px;
  border: 1px dashed rgba(130, 174, 184, 0.3);
  border-radius: 8px;
  font-size: 0.78rem;
}

.top-stats-note span {
  color: #9fb4ba !important;
}

.top-stats-note .note-error {
  color: #ffb4a8 !important;
}

.note-link {
  color: var(--kop-accent) !important;
  font-size: 0.78rem;
}

.eyebrow {
  color: var(--kop-accent) !important;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.top-stats-heading strong {
  color: white;
  font-size: 0.88rem;
}

.top-stats-row {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
}

.stat-card {
  display: flex;
  min-width: 0;
  min-height: 54px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 7px 8px;
  border: 1px solid rgba(130, 174, 184, 0.24);
  border-radius: 8px;
  background: rgba(8, 23, 30, 0.78);
  text-align: center;
  transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
}

.stat-card:hover {
  background: rgba(16, 48, 56, 0.92);
  border-color: rgba(255, 191, 102, 0.34);
  transform: translateY(-1px);
}

.stat-label {
  color: #9fb4ba !important;
  font-size: 0.66rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.stat-value {
  max-width: 100%;
  overflow: hidden;
  color: white;
  font-size: 0.88rem;
  line-height: 1.15;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mvp-strip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 30px;
  padding: 5px 10px;
  border: 1px solid rgba(255, 191, 102, 0.28);
  border-radius: 8px;
  background: rgba(33, 61, 66, 0.62);
}

.mvp-label {
  color: var(--kop-accent) !important;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.mvp-value {
  min-width: 0;
  overflow: hidden;
  color: white;
  font-size: 0.86rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 1199px) {
  .top-stats-row {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .top-stats-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .top-stats-heading {
    align-items: center;
  }
}
</style>
